import { useState, type FormEvent, type ReactNode } from 'react';
import { BellRing, Crosshair, ShieldAlert } from 'lucide-react';
import type { Device } from '@findmy/shared';
import { useCommands, useSendCommand, useSettings } from '../api/queries';
import { describeCommand } from '../lib/commands';
import { Dialog } from './Dialog';
import { Button, ErrorText } from './ui';

const DEFAULT_MESSAGE = 'This device is lost. If you find it, please get in touch using the details below.';

const inputClass =
  'mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800';

function reasonDisabled(device: Device, capability: string): string | null {
  if (device.revoked) return 'This device has been removed.';
  if (!device.capabilities.includes(capability)) return 'This device’s agent doesn’t support this.';
  return null;
}

function CommandButton({
  icon,
  label,
  disabledReason,
  busy,
  onClick,
  testId,
}: {
  icon: ReactNode;
  label: string;
  disabledReason: string | null;
  busy: boolean;
  onClick: () => void;
  testId: string;
}) {
  return (
    <Button
      disabled={disabledReason !== null || busy}
      title={disabledReason ?? undefined}
      onClick={onClick}
      data-testid={testId}
    >
      {icon}
      {label}
    </Button>
  );
}

function LostModeForm({ device, onDone }: { device: Device; onDone: () => void }) {
  const send = useSendCommand(device.id);
  const { data: settings } = useSettings();
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    send.mutate(
      {
        type: 'lost_mode_on',
        payload: { message: String(form.get('message')), contact: String(form.get('contact')) },
      },
      { onSuccess: onDone },
    );
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-slate-600 dark:text-slate-400">
        <strong>{device.name}</strong> will show this message full screen, even when locked, and keep sharing
        its location. It stays in lost mode until you turn it off here.
      </p>
      <label className="block text-sm font-medium">
        Message
        <textarea
          name="message"
          required
          maxLength={300}
          rows={3}
          defaultValue={device.lostMessage ?? DEFAULT_MESSAGE}
          className={inputClass}
        />
      </label>
      <label className="block text-sm font-medium">
        How to reach you (shown on the device)
        <input
          name="contact"
          maxLength={120}
          defaultValue={device.lostContact ?? settings?.defaultLostContact ?? ''}
          placeholder="e.g. 07700 900123 or you@example.com"
          className={inputClass}
        />
      </label>
      {send.error && <ErrorText>{send.error.message}</ErrorText>}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={send.isPending}>
          {device.isLost ? 'Update message' : 'Turn on lost mode'}
        </Button>
      </div>
    </form>
  );
}

/** Ring, Locate now and Lost mode, with what happened to the most recent command. */
export function DeviceCommands({ device }: { device: Device }) {
  const { data: commands } = useCommands(device.id);
  const send = useSendCommand(device.id);
  // Loaded now so the lost-mode form can pre-fill your default contact when it opens.
  useSettings();
  const [lostOpen, setLostOpen] = useState(false);
  const latest = commands?.[0];
  const locateBlocked =
    reasonDisabled(device, 'locate_now') ??
    (device.sharingPaused ? 'Location sharing is paused on this device.' : null);

  return (
    <div className="space-y-3">
      {device.isLost && (
        <div className="space-y-2 rounded-xl bg-amber-50 p-3 text-sm ring-1 ring-amber-300 dark:bg-amber-950/40 dark:ring-amber-800">
          <p className="font-medium text-amber-900 dark:text-amber-200">Lost mode is on</p>
          <p className="text-amber-900 dark:text-amber-100">“{device.lostMessage}”</p>
          {device.lostContact && (
            <p className="text-amber-800 dark:text-amber-300">Contact: {device.lostContact}</p>
          )}
          <div className="flex gap-2">
            <Button
              variant="secondary"
              disabled={send.isPending}
              onClick={() => send.mutate({ type: 'lost_mode_off' })}
              data-testid="lost-off"
            >
              Turn off lost mode
            </Button>
            <Button variant="ghost" onClick={() => setLostOpen(true)}>
              Edit message
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <CommandButton
          icon={<BellRing className="h-4 w-4" />}
          label="Ring"
          testId="ring"
          busy={send.isPending}
          disabledReason={reasonDisabled(device, 'ring')}
          onClick={() => send.mutate({ type: 'ring' })}
        />
        <CommandButton
          icon={<Crosshair className="h-4 w-4" />}
          label="Locate now"
          testId="locate-now"
          busy={send.isPending}
          disabledReason={locateBlocked}
          onClick={() => send.mutate({ type: 'locate_now' })}
        />
        {!device.isLost && (
          <CommandButton
            icon={<ShieldAlert className="h-4 w-4" />}
            label="Lost mode"
            testId="lost-mode"
            busy={send.isPending}
            disabledReason={reasonDisabled(device, 'lost_mode')}
            onClick={() => setLostOpen(true)}
          />
        )}
      </div>

      {send.error && <ErrorText>{send.error.message}</ErrorText>}
      {latest && !send.error && (
        <p
          role="status"
          data-testid="command-status"
          className={`text-sm ${latest.status === 'failed' || latest.status === 'expired' ? 'text-red-700 dark:text-red-400' : 'text-slate-600 dark:text-slate-400'}`}
        >
          {describeCommand(latest)}
        </p>
      )}

      <Dialog
        open={lostOpen}
        onClose={() => setLostOpen(false)}
        title={device.isLost ? 'Lost mode message' : 'Turn on lost mode'}
      >
        <LostModeForm device={device} onDone={() => setLostOpen(false)} />
      </Dialog>
    </div>
  );
}
